import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Box
} from '@mui/material';
import { EnergyChart } from './EnergyChart';

interface EnhancedAnalysisTabProps {
  analysisData: EnhancedAnalysisData | null;
  loading: boolean;
  error: string | null;
}

interface EnhancedAnalysisData {
  site_id: number;
  site_name: string;
  analysis_period: {
    start_date: string;
    end_date: string;
    days_analyzed: number;
  };
  calculations: Array<{
    calculation_id: string;
    calculation_name: string;
    chart_type: string;
    tag_name: string;
    tag_description: string;
    tag_units: string;
    period_total: number;
    daily_data: Array<{
      date: string;
      energy: number | null;
      reset_detected: boolean;
      data_points: number;
      cumulative_energy?: number;
      original_daily?: number;
    }>;
    data_points: number;
    previous_year?: {
      period_total: number;
      daily_data: any[];
      data_points: number;
      change_percent: number;
    };
    trends?: {
      trend_direction: string;
      average: number;
      moving_average: Array<{
        date: string;
        moving_average: number;
      }>;
      min_value: number;
      max_value: number;
    };
  }>;
}

export const EnhancedAnalysisTab: React.FC<EnhancedAnalysisTabProps> = ({
  analysisData,
  loading,
  error
}) => {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading enhanced analytics...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!analysisData) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No analysis data available
      </Alert>
    );
  }

  return (
    <Grid container spacing={3}>
      {analysisData.calculations.map((calc, index) => (
        <Grid item xs={12} key={calc.calculation_id}>
          <EnergyChart
            data={calc.daily_data}
            title={`${calc.calculation_name} - Enhanced Analysis`}
            units={calc.tag_units}
            chartType={calc.chart_type === 'cumulative' ? 'line' : 'line'}
            showResetIndicators={false}  // Remove meter reset indicators as requested
          />
          
          {/* Additional analysis info */}
          {/* Removed Previous Year Comparison and Trend Analysis sections for cleaner UI and better performance */}
        </Grid>
      ))}
    </Grid>
  );
};
